import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Calculator,
  PiggyBank,
  Wallet,
  TrendingUp,
  Scale,
  Home,
  Building2,
  Briefcase,
  FileText,
  Target,
  BarChart3,
} from 'lucide-react';

interface Tool {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  category: 'loans' | 'investments' | 'property' | 'tax' | 'planning';
}

const tools: Tool[] = [
  { id: 'risk-tolerance', name: 'Risk Tolerance Assessment', description: 'Evaluate your investment risk tolerance', icon: Briefcase, category: 'planning' },
  { id: 'loan-calculator', name: 'Loan Calculator', description: 'Monthly payments, interest, and total cost', icon: Calculator, category: 'loans' },
  { id: 'investment-calculator', name: 'Investment Calculator', description: 'Project growth with different scenarios', icon: TrendingUp, category: 'investments' },
  { id: 'budget-planner', name: 'Budget Planner', description: 'Create and manage your monthly budget', icon: Wallet, category: 'planning' },
  { id: 'emergency-fund', name: 'Emergency Fund Calculator', description: 'How much to save for emergencies', icon: PiggyBank, category: 'planning' },
  { id: 'debt-payoff', name: 'Debt Payoff Planner', description: 'Plan to become debt-free faster', icon: Scale, category: 'loans' },
  { id: 'capital-gains', name: 'Capital Gains Calculator', description: 'Calculate Capital Gains Tax liability', icon: Building2, category: 'tax' },
  { id: 'mortgage-calculator', name: 'Mortgage Calculator', description: 'Mortgage payments and total costs', icon: Home, category: 'property' },
  { id: 'property-investment', name: 'Property Investment Calculator', description: 'Analyze potential property investments', icon: Building2, category: 'property' },
  { id: 'retirement-calculator', name: 'Retirement Calculator', description: 'Plan savings and income needs', icon: Target, category: 'planning' },
  { id: 'tax-loss-harvesting', name: 'Tax-Loss Harvesting Calculator', description: 'Optimize tax efficiency through selling', icon: FileText, category: 'tax' },
  { id: 'cost-basis', name: 'Cost Basis Calculator', description: 'Investment cost basis and gains', icon: BarChart3, category: 'investments' },
  { id: 'redundancy-calculator', name: 'Redundancy Calculator', description: 'UK statutory redundancy pay and total package', icon: Briefcase, category: 'planning' },
];

const categories = [
  { id: 'all', name: 'All' },
  { id: 'loans', name: 'Loans & Debt' },
  { id: 'investments', name: 'Investments' },
  { id: 'property', name: 'Property' },
  { id: 'tax', name: 'Tax' },
  { id: 'planning', name: 'Planning' },
];

export default function Tools() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTools = tools.filter((tool) => {
    const matchCategory = selectedCategory === 'all' || tool.category === selectedCategory;
    const matchSearch =
      tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
              Financial planning tools
            </h1>
            <p className="mt-3 text-slate-600 max-w-2xl">
              Calculators and planners to help you make smarter financial decisions.
            </p>
      </div>

      {/* Search and filters */}
      <div className="space-y-6">
            <Input
              placeholder="Search tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-md border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-brand-900"
            />
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={
                    selectedCategory === cat.id
                      ? 'rounded-lg bg-brand-900 hover:bg-brand-800'
                      : 'rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
                  }
                >
                  {cat.name}
                </Button>
              ))}
            </div>
      </div>

      {/* Tool cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Card
                  key={tool.id}
                  className="border border-slate-200/80 shadow-sm bg-white rounded-2xl hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80 transition-all duration-200 cursor-pointer group"
                  onClick={() => navigate(`/tools/${tool.id}`)}
                >
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-200">
                      <Icon className="w-6 h-6 text-slate-700" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">{tool.name}</h3>
                    <p className="text-slate-600 text-sm mb-4 leading-relaxed">{tool.description}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                    >
                      Use calculator
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
      </div>

      {filteredTools.length === 0 && (
        <p className="text-center text-slate-500 py-12">No tools match your search.</p>
      )}

      {/* Help card */}
      <Card className="border border-slate-200/80 shadow-sm bg-slate-50/50 rounded-2xl">
        <CardContent className="pt-8 pb-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-6">Need help choosing?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <h3 className="font-medium text-slate-900 mb-2">Planning your budget?</h3>
              <p className="text-slate-600 leading-relaxed">
                Use the Budget Planner to create a monthly budget and track spending.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-slate-900 mb-2">Managing debt?</h3>
              <p className="text-slate-600 leading-relaxed">
                Try the Debt Payoff Planner to create a strategy for becoming debt-free.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-slate-900 mb-2">Buying property?</h3>
              <p className="text-slate-600 leading-relaxed">
                Use the Mortgage Calculator to estimate payments and total costs.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
